import { AreaChart } from "@mantine/charts";
import { Box, Divider, Flex, Select, Stack, Title } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { ArrowDown2 } from "iconsax-react";
import { range } from "lodash";
import { useQueryState } from "nuqs";

import { builder } from "@/builders";
import {
  useFakeAdminDashboardData,
  UserGrowth,
} from "@/builders/types/admin-dashboard";
import { EmptySlot } from "@/components/staff/shared/empty-slot";
import { useCurrentORG } from "@/components/super-admin/org-management/use-organisation";

import { ActiveUsers } from "./active-users";
import { NewUserSignUps } from "./new-signups";

import clsx from "clsx";
import dayjs from "dayjs";

export function KeyMetrics() {
  const { organization, setOrganization } = useCurrentORG();

  const [metric, setMetric] = useQueryState("metric", {
    defaultValue: "Monthly",
  });

  const initialDashboardData = useFakeAdminDashboardData();

  const { data, isPlaceholderData } = useQuery({
    queryKey: builder.data.admin_dashboard.$get({
      organization: organization.id,
    }),
    queryFn: () =>
      builder.$use.data.admin_dashboard({
        organization: organization.id,
      }),
    select: ({ data }) => {
      const months = range(0, dayjs().add(1, "month").month()).map((month) => {
        const key = dayjs().month(month).format("MMMM") as keyof UserGrowth;

        return {
          date: dayjs().month(month).format("MMM"),
          value: data.user_growth[key],
        };
      });

      return months;
    },
    placeholderData: {
      data: initialDashboardData,
    },
  });

  return (
    <Stack
      px={20}
      py={24}
      flex={1}
      className="rounded-lg bg-gradient-scales"
      bgr="no-repeat"
      style={{
        backdropFilter: "50px",
        backgroundOrigin: "border-box",
        backgroundClip: "padding-box",
        backgroundSize: "contain",
        backgroundPosition: "top right",
        backgroundRepeat: "no-repeat",
      }}
    >
      <Stack gap={16}>
        <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
          <Title
            order={2}
            className={clsx("prose-xl/medium text-primary-background-white", {
              skeleton: isPlaceholderData,
            })}
          >
            Key User Metrics
          </Title>

          <Select
            ml="auto"
            maw={150}
            pointer
            value={metric}
            onChange={(value) => {
              setMetric(value);
            }}
            withCheckIcon={false}
            data={["Monthly", "Weekly", "Yearly"]}
            defaultValue="Monthly"
            rightSection={
              <ArrowDown2 size="12" color="var(--primary-background-white)" />
            }
            classNames={{
              input:
                "bg-transparent text-primary-background-white border-primary-background-white",
            }}
          />
        </Flex>

        <Flex wrap="wrap" gap={16}>
          <ActiveUsers />
          <NewUserSignUps />
        </Flex>

        <Flex align="center">
          <Divider flex={1} />
          <Box
            p={8}
            className={clsx(
              "prose-sm/medium text-primary-background-white bg-gradient-cross-fade",
              "rounded-lg border border-primary-border-light backdrop-blur-[50px]",
              {
                skeleton: isPlaceholderData,
              }
            )}
          >
            User Growth
          </Box>
          <Divider flex={1} />
        </Flex>
        {data?.length ? (
          <Flex
            justify="center"
            className={clsx({ skeleton: isPlaceholderData })}
            align="center"
          >
            <AreaChart
              h={200}
              data={data}
              dataKey="date"
              withYAxis={false}
              series={[{ name: "value", color: "var(--accent-8)" }]}
              curveType="linear"
              gridAxis="y"
            />
          </Flex>
        ) : (
          <EmptySlot
            title="No data found"
            description="Create a course to get started"
          />
        )}
      </Stack>
    </Stack>
  );
}
